import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, ArrowDownToLine, ArrowUpFromLine, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

const emptyProd = { codigo: "", nombre: "", unidad: "un", metodo: "ppp", observaciones: "" };
const emptyMov = {
  tipo: "entrada", fecha: todayISO(), cantidad: "", costo_unitario: "", glosa: "",
  cuenta_contrapartida_codigo: "5.1.01", factura_compra_id: "", sin_asiento_factura: false,
};

export default function Existencias() {
  const [productos, setProductos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [movs, setMovs] = useState([]);
  const [openProd, setOpenProd] = useState(false);
  const [openMov, setOpenMov] = useState(false);
  const [selProd, setSelProd] = useState(null);
  const [formProd, setFormProd] = useState(emptyProd);
  const [formMov, setFormMov] = useState(emptyMov);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r, m] = await Promise.all([
        api.get("/existencias/productos"),
        api.get("/existencias/resumen"),
        api.get("/existencias/movimientos", { params: { limit: 100 } }),
      ]);
      setProductos(p.data);
      setResumen(r.data);
      setMovs(m.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const prodPage = useClientPagination(productos, `${productos.length}`);
  const movPage = useClientPagination(movs, `${movs.length}`);

  const submitProd = async (e) => {
    e.preventDefault();
    try {
      await api.post("/existencias/productos", formProd);
      toast.success("Producto creado");
      setOpenProd(false);
      setFormProd(emptyProd);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openMovimiento = (prod, tipo) => {
    setSelProd(prod);
    setFormMov({ ...emptyMov, tipo, fecha: todayISO() });
    setOpenMov(true);
  };

  const submitMov = async (e) => {
    e.preventDefault();
    if (!selProd) return;
    try {
      const payload = {
        producto_id: selProd.id,
        tipo: formMov.tipo,
        fecha: formMov.fecha,
        cantidad: parseFloat(formMov.cantidad) || 0,
        glosa: formMov.glosa,
        generar_asiento: !(formMov.tipo === "entrada" && formMov.sin_asiento_factura),
      };
      if (formMov.tipo === "entrada") {
        payload.costo_unitario = parseFloat(formMov.costo_unitario) || 0;
        payload.cuenta_contrapartida_codigo = formMov.cuenta_contrapartida_codigo || "5.1.01";
        if (formMov.factura_compra_id.trim()) {
          payload.factura_compra_id = formMov.factura_compra_id.trim();
        }
      }
      const { data } = await api.post("/existencias/movimientos", payload);
      toast.success(
        `${formMov.tipo === "entrada" ? "Entrada" : "Salida"} registrada · costo ${fmtCLP(data.movimiento.costo_total)}` +
        (data.asiento ? " · asiento en borrador" : "") +
        (data.aviso && !data.asiento ? ` · ${data.aviso}` : "")
      );
      setOpenMov(false);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto sin stock ni movimientos?")) return;
    try {
      await api.delete(`/existencias/productos/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Existencias"
        subtitle="Inventario con PPP o FIFO · entradas y salidas generan asientos de costo (borrador)"
        testid="inv-header"
        actions={
          <Dialog open={openProd} onOpenChange={setOpenProd}>
            <DialogTrigger asChild>
              <button data-testid="inv-nuevo" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">
                <Plus className="h-4 w-4" /> Nuevo producto
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuevo producto</DialogTitle>
                <DialogDescription>Define el método de valorización (PPP o FIFO).</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitProd} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">Código
                    <input required value={formProd.codigo} onChange={(e) => setFormProd({ ...formProd, codigo: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="inv-codigo" />
                  </label>
                  <label className="block text-sm">Unidad
                    <input value={formProd.unidad} onChange={(e) => setFormProd({ ...formProd, unidad: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                  </label>
                </div>
                <label className="block text-sm">Nombre
                  <input required value={formProd.nombre} onChange={(e) => setFormProd({ ...formProd, nombre: e.target.value })}
                    className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="inv-nombre" />
                </label>
                <label className="block text-sm">Método
                  <Select value={formProd.metodo} onValueChange={(v) => setFormProd({ ...formProd, metodo: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ppp">PPP (promedio ponderado)</SelectItem>
                      <SelectItem value="fifo">FIFO</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <DialogFooter>
                  <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm" data-testid="inv-guardar">Guardar</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {resumen && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <KPI label="Productos" value={resumen.productos} />
          <KPI label="Con stock" value={resumen.con_stock} />
          <KPI label="Valor inventario" value={fmtCLP(resumen.valor_total)} tone="primary" />
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <>
          <div className="bg-card border border-border rounded-md overflow-x-auto mb-4">
            <div className="px-4 py-2 border-b border-border label-caps">Productos</div>
            <table className="acc-table" data-testid="inv-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Método</th>
                  <th className="num">Stock</th>
                  <th className="num">Costo prom.</th>
                  <th className="num">Valor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {prodPage.pageItems.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-muted-foreground text-sm py-6">Sin productos</td></tr>
                ) : prodPage.pageItems.map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono text-[12px]">{p.codigo}</td>
                    <td className="font-medium">{p.nombre}</td>
                    <td className="text-[11px] uppercase">{p.metodo}</td>
                    <td className="num">{p.stock} {p.unidad}</td>
                    <td className="num">{fmtCLP(p.costo_promedio)}</td>
                    <td className="num font-semibold">{fmtCLP(p.valor_inventario)}</td>
                    <td>
                      <div className="flex gap-0.5">
                        <button onClick={() => openMovimiento(p, "entrada")} title="Entrada" className="text-primary hover:bg-primary/10 p-1 rounded-sm" data-testid={`inv-entrada-${p.codigo}`}>
                          <ArrowDownToLine className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => openMovimiento(p, "salida")} title="Salida / costo" className="text-warning-foreground hover:bg-warning/15 p-1 rounded-sm" data-testid={`inv-salida-${p.codigo}`}>
                          <ArrowUpFromLine className="h-3.5 w-3.5" />
                        </button>
                        {p.stock === 0 && (
                          <button onClick={() => eliminar(p.id)} title="Eliminar" className="text-destructive hover:bg-destructive/10 p-1 rounded-sm">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination pagination={prodPage.pagination} pageSize={prodPage.pageSize} onPageSizeChange={prodPage.setPageSize} onPageChange={prodPage.setPage} testidPrefix="inv-prod" />
          </div>

          <div className="bg-card border border-border rounded-md overflow-x-auto">
            <div className="px-4 py-2 border-b border-border label-caps">Últimos movimientos (kardex)</div>
            <table className="acc-table" data-testid="inv-movs">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Producto</th>
                  <th className="num">Cantidad</th>
                  <th className="num">Costo u.</th>
                  <th className="num">Total</th>
                  <th className="num">Stock</th>
                  <th>Asiento</th>
                </tr>
              </thead>
              <tbody>
                {movPage.pageItems.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted-foreground text-sm py-6">Sin movimientos</td></tr>
                ) : movPage.pageItems.map((m) => (
                  <tr key={m.id}>
                    <td>{fmtDate(m.fecha)}</td>
                    <td className="text-[11px] uppercase font-semibold">{m.tipo}</td>
                    <td className="text-[12px]">{m.producto_codigo} · {m.producto_nombre}</td>
                    <td className="num">{m.cantidad}</td>
                    <td className="num">{fmtCLP(m.costo_unitario)}</td>
                    <td className="num font-semibold">{fmtCLP(m.costo_total)}</td>
                    <td className="num">{m.stock_despues}</td>
                    <td className="text-[11px] text-muted-foreground">{m.asiento_id ? "borrador" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination pagination={movPage.pagination} pageSize={movPage.pageSize} onPageSizeChange={movPage.setPageSize} onPageChange={movPage.setPage} testidPrefix="inv-mov" />
          </div>
        </>
      )}

      <Dialog open={openMov} onOpenChange={setOpenMov}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{formMov.tipo === "entrada" ? "Entrada de inventario" : "Salida / costo de ventas"}</DialogTitle>
            <DialogDescription>
              {selProd ? `${selProd.codigo} · ${selProd.nombre} · stock ${selProd.stock}` : ""}
              {formMov.tipo === "entrada"
                ? " · Debe Existencias / Haber Costo o compras (5.1.01); no use Proveedores si ya hay factura"
                : " · Debe Costo de mercadería / Haber Existencias"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMov} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">Fecha
                <input type="date" required value={formMov.fecha} onChange={(e) => setFormMov({ ...formMov, fecha: e.target.value })}
                  className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
              </label>
              <label className="block text-sm">Cantidad
                <input type="number" required min="0.001" step="any" value={formMov.cantidad}
                  onChange={(e) => setFormMov({ ...formMov, cantidad: e.target.value })}
                  className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="inv-cantidad" />
              </label>
            </div>
            {formMov.tipo === "entrada" && (
              <>
                <label className="block text-sm">Costo unitario
                  <input type="number" required min="0" step="any" value={formMov.costo_unitario}
                    onChange={(e) => setFormMov({ ...formMov, costo_unitario: e.target.value })}
                    className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" data-testid="inv-costo" />
                </label>
                <label className="block text-sm">ID factura de compra (opcional)
                  <input value={formMov.factura_compra_id}
                    onChange={(e) => setFormMov({ ...formMov, factura_compra_id: e.target.value })}
                    className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm"
                    placeholder="Si la factura ya cargó 1.1.08" data-testid="inv-factura-id" />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={formMov.sin_asiento_factura}
                    onChange={(e) => setFormMov({ ...formMov, sin_asiento_factura: e.target.checked })}
                    data-testid="inv-sin-asiento" />
                  No generar asiento (factura de mercaderías ya debitó existencias)
                </label>
                {!formMov.sin_asiento_factura && (
                  <label className="block text-sm">Contrapartida (código)
                    <input value={formMov.cuenta_contrapartida_codigo}
                      onChange={(e) => setFormMov({ ...formMov, cuenta_contrapartida_codigo: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm"
                      placeholder="5.1.01" />
                  </label>
                )}
              </>
            )}
            <label className="block text-sm">Glosa
              <input value={formMov.glosa} onChange={(e) => setFormMov({ ...formMov, glosa: e.target.value })}
                className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
            </label>
            <DialogFooter>
              <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm" data-testid="inv-mov-guardar">
                Registrar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-foreground", primary: "text-primary" };
  return (
    <div className="bg-card border border-border rounded-md p-3">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
